// src/pages/admin/CourseLectures/lectures/Lecture01Vorbereitung.tsx
import React, { useState, useRef } from "react";
import {
  Checklist,
  InfoBox,
  AgendaCard,
  FileNamingGrid,
  TaskList,
  EduTable,
  EmailExample,
} from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${isOpen ? "border-[#009fe3]/50" : "border-gray-200"} bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-gray-50"}`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"}`}
        >
          <span>{icon}</span>
          <span className="hidden xs:inline sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${isOpen ? "text-[#0369a1]" : "text-gray-800"}`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const Lecture01Vorbereitung: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      <AgendaCard
        items={[
          { num: "1", label: "Vorstellung" },
          { num: "2", label: "Zeugnisse Scannen" },
          { num: "3", label: "Übersetzung" },
          { num: "4", label: "Organisieren" },
          { num: "5", label: "Bewerbungsfoto" },
          { num: "6", label: "Email Adresse" },
        ]}
      />

      {/* 01 */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="👤"
        title="Vorstellung"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Was du für deinen Ausbildungsweg brauchst — ein kontinuierlicher
          Prozess:
        </p>
        <Checklist
          items={[
            <>
              <strong>SSC</strong> — Secondary School Certificate
            </>,
            <>
              <strong>HSC / Diploma / Bachelor</strong>
            </>,
            <>
              <strong>B1 Certificate</strong> — Deutschkenntnisse Nachweis
            </>,
            <>
              <strong>B2 Language</strong> — Sprachkompetenz weiterentwickeln
            </>,
            <>
              <strong>B2 Certificate</strong> — Offizieller Nachweis
            </>,
            <>
              <strong>Language skill</strong> — Kontinuierlicher Prozess
            </>,
          ]}
        />
        <InfoBox icon="💡" title="Wichtig" variant="tip">
          Dies ist ein <strong>kontinuierlicher Prozess</strong>. Alle Schritte
          bauen aufeinander auf und sollten parallel verfolgt werden.
        </InfoBox>
      </Accordion>

      {/* 02 */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="🗂️"
        title="Zeugnisse Scannen"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Scanne deine <strong>Originaldokumente</strong> und speichere sie in
          Google Drive <em>und</em> lokal auf deinem Computer:
        </p>
        <Checklist
          items={[
            "Passport (Reisepass)",
            "SSC Marksheet",
            "SSC Certificate",
            "HSC Marksheet / Diploma Mark Sheets",
            "HSC Certificate / Diploma Certificates",
            "Bachelor Marksheets + Certificate",
            "Birth Certificate (Geburtsurkunde)",
          ]}
        />
      </Accordion>

      {/* 03 */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="🌐"
        title="Zeugnisse Übersetzen"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Schicke die Scans an einen{" "}
          <strong>staatlich beeidigten Dolmetscher</strong> zur beglaubigten
          Übersetzung ins Deutsche:
        </p>
        <Checklist
          items={[
            "SSC Marksheet",
            "SSC Certificate",
            "HSC Marksheet / Diploma Mark Sheets",
            "HSC Certificate / Diploma Certificates",
            "Bachelor Marksheets + Certificate",
            "Birth Certificate",
          ]}
        />
        <InfoBox icon="⚠️" title="Hinweis" variant="warning">
          Nur von der Regierung zertifizierte Dolmetscher dürfen beglaubigte
          Übersetzungen ausstellen. Die Kopie der Scans reicht — schicke keine
          Originale per Post.
        </InfoBox>

        {/* Translation example images — full width, stacked one by one */}
        <div className="mt-4">
          <p className="text-xs font-bold text-[#0369a1] uppercase tracking-wide mb-3">
            📄 Vorlagen — So sehen beglaubigte Übersetzungen aus
          </p>
          <div className="flex flex-col gap-4">
            {[
              {
                src: "/assets/img/lectures/uebersetzung_ssc_zeugnis.jpeg",
                label: "SSC Zeugnis — Übersetzung",
              },
              {
                src: "/assets/img/lectures/uebersetzung_hsc_zeugnis.jpeg",
                label: "HSC Zeugnis — Übersetzung",
              },
              {
                src: "/assets/img/lectures/uebersetzung_ssc_notenspiegel.jpeg",
                label: "SSC Notenspiegel — Übersetzung",
              },
              {
                src: "/assets/img/lectures/uebersetzung_hsc_notenspiegel.jpeg",
                label: "HSC Notenspiegel — Übersetzung",
              },
            ].map((img, i) => (
              <div
                key={i}
                className="rounded-xl border border-gray-200 overflow-hidden shadow-sm"
              >
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-600">
                    {img.label}
                  </span>
                </div>
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full object-contain bg-white"
                />
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 04 */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="📁"
        title="Organisieren"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm font-semibold text-gray-700 mb-2">
          Ordnerstruktur erstellen:
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-sm">
          <div className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
            <span>📂</span>
            <span>Zeugnissen &amp; Dokumenten</span>
            <span className="text-[11px] text-gray-400 font-normal">
              (Hauptordner)
            </span>
          </div>
          <div className="pl-6 space-y-1.5">
            {["📁 Original", "📁 Uebersetzungen", "📁 Bewerbungsfoto"].map(
              (folder) => (
                <div
                  key={folder}
                  className="flex items-center gap-2 text-gray-600 text-sm"
                >
                  <span className="text-gray-300">└─</span>
                  <span>{folder}</span>
                </div>
              ),
            )}
          </div>
        </div>
        <p className="text-sm font-semibold text-gray-700 mb-1">
          Dateinamen-Konvention:
        </p>
        <FileNamingGrid
          items={[
            {
              label: "Original",
              codes: [
                "SSC_Notenspiegel_Vorname_Nachname",
                "HSC_Zeugnis_Vorname_Nachname",
              ],
            },
            {
              label: "Übersetzt + Merged",
              codes: [
                "SSC_Zeugnis_Notenspiegel_Vorname_Nachname",
                "HSC_Zeugnis_Notenspiegel_Vorname_Nachname",
                "Bachelor_Zeugnis_Notenspiegel_Vorname_Nachname",
              ],
            },
            {
              label: "Diploma (Übersetzung + Original)",
              codes: ["Diploma_Zeugnis_Uebersetzung_Vorname_Nachname"],
              fullWidth: false,
            },
            {
              label: "Finale Gesamtdatei (1 PDF)",
              codes: ["Zeugnisse_mitUebersetzung_Vorname_Nachname"],
              note: "Reihenfolge: Master → Bachelor → HSC/Diploma → SSC (erst Zeugnis, dann Notenspiegel)",
              fullWidth: true,
            },
          ]}
        />
        <p className="text-sm font-semibold text-gray-700 mt-4 mb-2">
          Nützliche Online-Tools:
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { label: "🔗 iLovePDF.com", href: "https://ilovepdf.com" },
            { label: "🔗 smallpdf.com", href: "https://smallpdf.com" },
            { label: "🔀 Merge PDF" },
            { label: "✂️ Split PDF" },
            { label: "📦 Compress PDF" },
            { label: "🔄 Format konvertieren" },
          ].map((t, i) => (
            <div
              key={i}
              className="px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700"
            >
              {t.href ? (
                <a
                  href={t.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#009fe3] hover:underline"
                >
                  {t.label}
                </a>
              ) : (
                t.label
              )}
            </div>
          ))}
        </div>
        <InfoBox variant="info">
          Um PDFs zusammenzufügen: Suche „Merge PDF" bei Google. Für
          Komprimieren: „Compress PDF". Für Konvertieren: „JPG to PDF" etc.
        </InfoBox>
      </Accordion>

      {/* 05 */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="📸"
        title="Bewerbungsfoto"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Dein Bewerbungsfoto ist der erste Eindruck. So sollte es aussehen:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {[
            {
              src: "/assets/img/lectures/bewerbungsfoto_1.jpeg",
              label: "Männlich – Professionell",
            },
            {
              src: "/assets/img/lectures/bewerbungsfoto_2.jpeg",
              label: "Männlich – Freundlich",
            },
            {
              src: "/assets/img/lectures/bewerbungsfoto_3.jpeg",
              label: "Weiblich – Professionell",
            },
            {
              src: "/assets/img/lectures/bewerbungsfoto_4.jpeg",
              label: "Weiblich – Freundlich",
            },
          ].map((photo, i) => (
            <div
              key={i}
              className="relative rounded-xl overflow-hidden border-2 border-gray-200 hover:border-[#009fe3] transition-all shadow-sm"
            >
              <img
                src={photo.src}
                alt={photo.label}
                className="w-full h-48 sm:h-52 object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-2">
                <span className="text-white text-xs font-semibold">
                  {photo.label}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          {[
            { emoji: "😊", label: "Freundliches Auftreten" },
            { emoji: "🤝", label: "Einladende Gestik" },
            { emoji: "😁", label: "Lächeln" },
            { emoji: "🔄", label: "Rechte Schulter leicht nach vorne" },
          ].map((tip, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm font-medium text-[#0369a1]"
            >
              <span className="text-xl flex-shrink-0">{tip.emoji}</span>
              {tip.label}
            </div>
          ))}
        </div>
        <InfoBox variant="tip" icon="💡" title="Profi-Tipp">
          Weißer oder hellgrauer Hintergrund, gepflegte Kleidung (Hemd/Bluse),
          natürliche Beleuchtung. Das Foto sollte professionell wirken — kein
          Selfie!
        </InfoBox>
      </Accordion>

      {/* 06 */}
      <Accordion
        id="06"
        num="ABSCHNITT 06"
        icon="✉️"
        title="Email Adresse"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Verwende deinen <strong>echten Vor- und Nachnamen</strong> gemäß
          Reisepass. Keine Spitznamen oder Zahlen!
        </p>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
          Empfohlene Formate:
        </p>
        <EmailExample
          address="vorname.nachname@gmail.com"
          tag="Empfohlen ★"
          tagColor="bg-green-100 text-green-700"
        />
        <EmailExample address="nachname.vorname@outlook.com" tag="Gut" />
        <EmailExample address="nachname@gmail.com" tag="Gut" />
        <EmailExample
          address="v.nachname@gmail.com"
          tag="OK"
          tagColor="bg-gray-100 text-gray-600"
        />
        <EmailExample
          address="vornamenachname@yahoo.com"
          tag="OK"
          tagColor="bg-gray-100 text-gray-600"
        />
        <p className="text-xs text-gray-400 mt-3">
          ✅ Erlaubte Domains: <strong>Gmail</strong> · <strong>Outlook</strong>{" "}
          · <strong>Yahoo</strong>
        </p>
      </Accordion>

      {/* BONUS - Bildungssystem */}
      <Accordion
        id="bonus-edu"
        num="BONUS"
        icon="🎓"
        title="Bildungssystem in Deutschland"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          So werden deine Abschlüsse im deutschen Bildungssystem anerkannt:
        </p>
        <EduTable
          rows={[
            { input: "SSC", output: "Realschulabschluss" },
            { input: "SSC + HSC", output: "Mittlere Reife" },
            {
              input: "SSC + Diploma",
              output: "Mittlere Reife + Berufliche Grundbildung",
            },
            {
              input: "SSC + HSC/Diploma + Uni (1 Jahr)",
              output: "Allgemeine Hochschulreife",
            },
          ]}
        />
        <InfoBox variant="info" icon="🔗" title="Anabin-Datenbank">
          Prüfe ob deine Universität als „H+" eingestuft ist:{" "}
          <a
            href="https://anabin.kmk.org/cms/public/startseite"
            target="_blank"
            rel="noreferrer"
            className="text-[#009fe3] underline font-medium"
          >
            anabin.kmk.org
          </a>
        </InfoBox>
      </Accordion>

      {/* BONUS - Ausbildungssuche */}
      <Accordion
        id="bonus-search"
        num="BONUS"
        icon="🔍"
        title="Nach Ausbildungsart suchen"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Finde den richtigen Ausbildungsberuf für dich:
        </p>
        <Checklist
          items={[
            "Finde heraus, was dich wirklich bewegt",
            "Denk an Professionen, die dir wirklich Spaß machen könnten",
            <>
              Nutze{" "}
              <a
                href="https://www.ausbildung.de/berufe/glossar/"
                target="_blank"
                rel="noreferrer"
                className="text-[#009fe3] underline"
              >
                ausbildung.de/berufe/glossar
              </a>{" "}
              zur Orientierung
            </>,
          ]}
        />
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="✅"
        title="Deine Aufgaben – Lektion 01"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              "Zeugnisse scannen und von einem staatlich zertifizierten Dolmetscher übersetzen lassen",
              "Zeugnisse gemäß der Namenskonvention organisieren und als PDF zusammenführen",
              "Professionelles Bewerbungsfoto aufnehmen (oder aufnehmen lassen)",
              "Professionelle E-Mail-Adresse mit Vor- und Nachname erstellen",
              "Eine realistische Liste mit 10 Berufen erstellen, die du gerne ausüben würdest",
              "Fokus auf B2-Sprachniveau sowie auf das offizielle Sprachzeugnis legen",
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture01Vorbereitung;
